// external
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// selectors
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as rollToSelectors from '@makemydeal/dr-dash-store/selectors/rollToSelectors';

// types
import { OfferTypeEnum } from '@makemydeal/dr-platform-types';

// custom internal components
import RollToPairsGrid from './RollToPairsGrid';
import RollToPaymentHeader from './RollToPaymentHeader';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { ROLL_TO_OPTIONS_LABELS } from '@makemydeal/dr-shared-types';
import RollToProfitIndicator from './RollToProfitIndicator';
import { ActivityText } from './constants/adjustableFields';

type RollToPaymentProfitProps = {
    activeTab: ROLL_TO_OPTIONS_LABELS;
};

const RollToPaymentProfit: FC<RollToPaymentProfitProps> = ({ activeTab }) => {
    const [showProfit, setShowProfit] = useState(false);
    const [showIndicator, setShowIndicator] = useState(false);
    const { profit } = useSelector(rollToSelectors.getRollToOptionsItems);
    const hasRollToOptions = useSelector(rollToSelectors.hasRollToOptionsItems);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const tradeProfit = profit?.tradeInProfit;
    const vehicleProfit = profit?.vehicleProfit;
    const frontProfit = profit?.frontEndProfit;
    const reserveProfit = profit?.reserveProfit || 0;
    const backProfit = profit?.backEndProfit || 0;
    const previousPayment = useSelector(offerSelectors.getMonthlyPayment);
    const payment = useSelector(rollToSelectors.getPayment);
    const previousProfit = useSelector(offerReduxSelectors.getSelectedPaymentProfitDetails);
    const previousTradeInProfit = previousProfit?.tradeInProfit;
    const previousVehicleProfit = previousProfit?.vehicleProfit;
    const previousBalanceDue = useSelector(offerSelectors.getBalanceDueAmountByOffer) || 0;
    const totalAmountDue = useSelector(rollToSelectors.getBalanceDue);
    const showHideProfits = () => {
        setShowProfit(!showProfit);
    };

    useEffect(() => {
        if (activeTab === ROLL_TO_OPTIONS_LABELS.PROFIT) {
            setShowIndicator(true);
        } else {
            setShowIndicator(showProfit);
        }
    }, [showProfit, activeTab]);

    const getDisplayValue = (value: number | string | undefined = ''): number | string => {
        if (activeTab === ROLL_TO_OPTIONS_LABELS.PROFIT) {
            return formatDollarsAndCents(value);
        }
        return showProfit ? formatDollarsAndCents(value) : ActivityText.Masking;
    };

    const actionMessage = showProfit ? ActivityText.Hide : ActivityText.Show;

    if (!hasRollToOptions) {
        return null;
    }

    return (
        <>
            {activeTab !== ROLL_TO_OPTIONS_LABELS.PROFIT && (
                <>
                    <RollToPaymentHeader />
                    <RollToPaymentHeader gridCategory={'Profit'} gridAction={showHideProfits} actionMessage={actionMessage} />
                    <RollToPairsGrid
                        headers={'Front'}
                        rows={[
                            {
                                label: 'Trade',
                                value: getDisplayValue(tradeProfit),
                                indicator: (
                                    <RollToProfitIndicator
                                        currentValue={tradeProfit}
                                        previousValue={previousTradeInProfit}
                                        showIndicator={showIndicator}
                                    />
                                )
                            },
                            {
                                label: 'Vehicle',
                                value: getDisplayValue(vehicleProfit),
                                dividerBelow: true,
                                indicator: (
                                    <RollToProfitIndicator
                                        currentValue={vehicleProfit}
                                        previousValue={previousVehicleProfit}
                                        showIndicator={showIndicator}
                                    />
                                )
                            },
                            {
                                label: 'Total Front',
                                value: getDisplayValue(frontProfit),
                                indicator: (
                                    <RollToProfitIndicator
                                        currentValue={frontProfit}
                                        previousValue={previousProfit?.frontEndProfit}
                                        showIndicator={showIndicator}
                                    />
                                )
                            }
                        ]}
                    />
                </>
            )}
            <RollToPaymentHeader />
            <RollToPairsGrid
                headers={'Back'}
                rows={[
                    {
                        label: 'Finance Reserve',
                        value: getDisplayValue(reserveProfit),
                        dividerBelow: true,
                        indicator: (
                            <RollToProfitIndicator
                                currentValue={reserveProfit}
                                previousValue={previousProfit?.reserveProfit}
                                showIndicator={showIndicator}
                            />
                        )
                    },
                    {
                        label: 'Total Back',
                        value: getDisplayValue(reserveProfit + backProfit),
                        indicator: (
                            <RollToProfitIndicator
                                currentValue={reserveProfit + backProfit}
                                previousValue={(previousProfit?.backEndProfit ?? 0) + (previousProfit?.reserveProfit ?? 0)}
                                showIndicator={showIndicator}
                            />
                        )
                    }
                ]}
            />
            <RollToPaymentHeader />
            {activeTab !== ROLL_TO_OPTIONS_LABELS.PAYMENT && (
                <>
                    <RollToPaymentHeader gridCategory={'Payment'} />
                    <RollToPairsGrid
                        rows={[
                            {
                                label: 'Payment',
                                value: formatDollarsAndCents(payment),
                                showDealPulseTooltip: true,
                                indicator: (
                                    <RollToProfitIndicator
                                        currentValue={payment}
                                        previousValue={previousPayment}
                                        showIndicator={true}
                                    />
                                )
                            }
                        ]}
                    />
                </>
            )}
            {offerType === OfferTypeEnum.FINANCE && activeTab !== ROLL_TO_OPTIONS_LABELS.BALANCE_DUE && (
                <>
                    <RollToPaymentHeader gridCategory={'Balance Due'} />
                    <RollToPairsGrid
                        rows={[
                            {
                                label: 'Total Balance Due',
                                value: formatDollarsAndCents(totalAmountDue),
                                indicator: (
                                    <RollToProfitIndicator
                                        currentValue={totalAmountDue}
                                        previousValue={previousBalanceDue}
                                        showIndicator={true}
                                    />
                                )
                            }
                        ]}
                    />
                </>
            )}
        </>
    );
};

export default RollToPaymentProfit;
